import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography, Stack, TextField, Fade, Alert } from '@mui/material';
import { Container, SubmitButton } from './style';
import { ColorsV2 } from 'app/constants/color';
import { routes } from 'app/routes';
import apiService from 'app/services/apiService';
import appService from 'app/services/appService';

const TOSAuth = () => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [codeError, setCodeError] = useState(0);

  const { t } = useTranslation('translation', { keyPrefix: 'tos_authenticate' });

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const handleChange = event => {
    setCode(event.target.value);
  };

  const validateCode = async () => {
    setIsLoading(true);
    let res = null;
    try {
      res = await apiService.validateTOSCode(queryParams.get('id'), code);
      if (res.data.status === 'success') {
        // Set user
        apiService.setIsUserTOS(true);
        appService.setCookie(res.data.accessToken);
        // redirect
        switch (res.data.service) {
          case 'gap-analysis':
            navigate(routes.TOSGAP);
            break;
          default:
            // Todo: Add default redirect
        }
      }
      if (res.data.status === 'failed') {
        // Notify user that code was incorrect
        setIsLoading(false);
        setCodeError(prev => prev + 1);
      }
    } catch (error) {
      console.error(error);
      // Display error
    }
  };

  useEffect(() => {
    // Send a validation request with empty code
    // A query parameter in the header (codeRequired) can now be read to determine if a code is needed before making the api call
    validateCode();
    // If failed then the user is prompted to enter a code
  }, []);

  return (
    <>
      <Container>
        <Typography variant='h4' color={ColorsV2.white} textAlign='center' pb='20px'>
          {t('main_header')}
        </Typography>
        {isLoading ? (
          <Typography sx={{ color: ColorsV2.white }}>{t('loading')}</Typography> // Update loading behaviour to be more seamless for the user, no need to hide the whole page when loading
        ) : (
          <Stack
            direction='column'
            alignItems='center'
            gap={2}
            sx={{
              backgroundColor: ColorsV2.primary,
              paddingLeft: '2em',
              paddingRight: '2em',
            }}
          >
            <Stack
              component='form'
              onSubmit={validateCode}
              noValidate
              direction='column'
              justifyContent='center'
              alignItems='center'
              sx={{
                backgroundColor: ColorsV2.secondary,
                border: `2px solid ${ColorsV2.outline}`,
                minWidth: '500px',
                paddingY: 2,
              }}
            >
              <Typography
                variant='h5'
                sx={{
                  color: ColorsV2.white,
                }}
              >
                {t('please_enter_code')}
              </Typography>
              <TextField
                variant='outlined'
                label={t('verification_code')}
                value={code}
                onChange={handleChange}
                //   error={emailError}
                //   helperText={emailError}
                required
                autoFocus
                inputProps={{ style: { color: ColorsV2.white } }}
                sx={{
                  marginTop: '20px',
                  marginBottom: '20px',
                  // Root class for the input field
                  '& .MuiOutlinedInput-root': {
                    color: ColorsV2.white,
                    // Class for the border around the input field
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: ColorsV2.primary,
                      borderWidth: '2px',
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'secondary.main',
                        borderWidth: '3px',
                      },
                    },
                  },
                  // Class for the label of the input field
                  '& .MuiInputLabel-outlined': {
                    color: ColorsV2.white,
                    fontWeight: 'bold',
                    '&.Mui-focused': {
                      color: ColorsV2.acent,
                    },
                  },
                }}
              ></TextField>
              <SubmitButton
                type='submit'
                sx={{
                  margin: '10px',
                  maxWidth: { lg: '180px', xl: '280px' },
                  backgroundColor: ColorsV2.acent,
                  color: ColorsV2.white,

                  '&:hover': {
                    backgroundColor: ColorsV2.primary,
                  },
                }}
              >
                {t('enter_with_code')}
              </SubmitButton>
            </Stack>
            <Fade in={codeError > 1}>
              <Alert
                severity='error'
                sx={{
                  width: '500px',
                }}
              >
                {t('incorrect_code')}
              </Alert>
            </Fade>
          </Stack>
        )}
      </Container>
    </>
  );
};

export default TOSAuth;
